import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import type { ImageObject } from '../../../types';
import { selectedTeethPreviewAtom } from '../../atoms';
import {
  useGetCalculusFindings,
  useGetCariesFindings,
  useGetPrlFindings,
  useGetRalFindings,
  useGetRblFindings,
} from '../../helpers';

export const useFilteredFindings = (currentImage: ImageObject) => {
  const selectedTeeth = useAtomValue(selectedTeethPreviewAtom);

  // Get all findings first
  const cariesFindingsAll = useGetCariesFindings({ image: currentImage });
  const calcFindingsAll = useGetCalculusFindings({ image: currentImage });
  const rblFindingsAll = useGetRblFindings({ image: currentImage });
  const prlFindingsAll = useGetPrlFindings({ image: currentImage });
  const ralFindingsAll = useGetRalFindings({ image: currentImage });

  // Helper function to check if a tooth finding should be included
  const shouldIncludeTooth = useMemo(() => {
    return (toothId?: string | number) => {
      if (!selectedTeeth?.length) return true;
      if (!toothId) return false;

      const toothStr = String(toothId).toUpperCase();

      // Check direct match (for when selection and findings use same format)
      if (selectedTeeth.includes(toothStr)) return true;

      // For more robust matching, we could normalize both sides, but since
      // the findings helpers already handle most normalization, direct string
      // comparison should work for most cases
      return selectedTeeth.some(
        (selectedTooth) => String(selectedTooth).toUpperCase() === toothStr,
      );
    };
  }, [selectedTeeth]);

  // Filter findings by selected teeth if provided
  const cariesFindings = useMemo(() => {
    if (!selectedTeeth?.length) return cariesFindingsAll;
    return cariesFindingsAll?.filter((finding) =>
      shouldIncludeTooth(finding.tooth),
    );
  }, [cariesFindingsAll, selectedTeeth, shouldIncludeTooth]);

  const calcFindings = useMemo(() => {
    if (!selectedTeeth?.length) return calcFindingsAll;
    return calcFindingsAll?.filter((finding) =>
      shouldIncludeTooth(finding.tooth),
    );
  }, [calcFindingsAll, selectedTeeth, shouldIncludeTooth]);

  const rblFindings = useMemo(() => {
    if (!selectedTeeth?.length) return rblFindingsAll;
    return rblFindingsAll?.filter((finding) =>
      shouldIncludeTooth(finding.tooth),
    );
  }, [rblFindingsAll, selectedTeeth, shouldIncludeTooth]);

  const prlFindings = useMemo(() => {
    if (!selectedTeeth?.length) return prlFindingsAll;
    return prlFindingsAll?.filter((finding) =>
      shouldIncludeTooth(finding.tooth),
    );
  }, [prlFindingsAll, selectedTeeth, shouldIncludeTooth]);

  const ralFindings = useMemo(() => {
    if (!selectedTeeth?.length) return ralFindingsAll;
    return ralFindingsAll?.filter((finding) =>
      shouldIncludeTooth(finding.tooth),
    );
  }, [ralFindingsAll, selectedTeeth, shouldIncludeTooth]);

  const showFindings = useMemo(() => {
    return !!(
      cariesFindings?.length ||
      calcFindings?.length ||
      rblFindings?.length ||
      prlFindings?.length ||
      ralFindings?.length
    );
  }, [cariesFindings, calcFindings, rblFindings, prlFindings, ralFindings]);

  return {
    cariesFindings,
    calcFindings,
    rblFindings,
    prlFindings,
    ralFindings,
    showFindings,
  };
};
